import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSubject,
  deleteSubject,
  getAllSubjectForClasses,
  getClassrooms,
  editClassroom,
  getSubjects,
  updateSubject,
  assignSubjectToClass,
  unassignSubjectToClass,
  getGradingSystem,
  updateGradingSystem,
  setGradeForStudent,
  saveTraitEvaluation,
  getStudentReport,
  updateStudentReport,
  getExpenseCatalog,
  createExpenseCatalog,
  updateExpenseCatalog,
  deleteExpenseCatalog,
  getExpenseVendors,
  createExpenseVendor,
  updateExpenseVendor,
  deleteExpenseVendor,
  getExpenses,
  createExpense,
  deleteExpense,
  getExpenseAnalytics,
  type ExpenseLedgerParams,
  type ExpenseAnalyticsParams,
} from "../services/admin";

interface Props {
  close: () => void;
}
export function useGetSubjectQuery() {
  return useQuery({
    queryKey: ["subject"],
    queryFn: () => getSubjects(),
  });
}
export function useGetSubjectForClassesQuery() {
  return useQuery({
    queryKey: ["subjectclass"],
    queryFn: () => getAllSubjectForClasses(),
  });
}
export function useCreateSubjectMutation({ close }: Props) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSubject,
    onError: () => {},
    onSuccess: () => {
      close();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["subject"] }).catch(() => {});
    },
  });
}
export function useUpdateSubjectMutation({ close }: Props) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSubject,
    onError: () => {},
    onSuccess: () => {
      close();
    },
    onSettled: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["subject"] }),
          queryClient.invalidateQueries({ queryKey: ["subjectclass"] }),
        ]);
      } catch (err) {
        console.error("Error invalidating queries", err);
      }
    },
  });
}

export function useDeleteSubjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSubject,
    onError: () => {},
    onSettled: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["subject"] }),
          queryClient.invalidateQueries({ queryKey: ["subjectclass"] }),
        ]);
      } catch (err) {
        console.error("Error invalidating queries", err);
      }
    },
  });
}
//classrooms
export function useGetClassroomsQuery() {
  return useQuery({
    queryKey: ["classrooms"],
    queryFn: () => getClassrooms(),
  });
}

export function useEditClassroomMutation(props?: { close?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editClassroom,
    onError: () => {},
    onSuccess: () => {
      if (props?.close) {
        props.close();
      }
    },
    onSettled: () => {
      queryClient
        .invalidateQueries({ queryKey: ["classrooms"] })
        .catch(() => {});
    },
  });
}

//assign subject to a classroom

export function useAssignSubjectToClassMutation({ close }: Props) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignSubjectToClass,
    onError: () => {},
    onSuccess: () => {
      close();
    },
    onSettled: () => {
      queryClient
        .invalidateQueries({ queryKey: ["subjectclass"] })
        .catch(() => {});
    },
  });
}

//unassign subject from a classroom
export function useUnassignSubjectToClassMutation({ close }: Props) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unassignSubjectToClass,
    onError: () => {},
    onSuccess: () => {
      close();
    },
    onSettled: () => {
      queryClient
        .invalidateQueries({ queryKey: ["subjectclass"] })
        .catch(() => {});
    },
  });
}

export function useGetGradeSystemQuery() {
  return useQuery({
    queryKey: ["grade"],
    queryFn: () => getGradingSystem(),
  });
}

export function useUpdateGradeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateGradingSystem,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["grade"] }).catch(() => {});
    },
  });
}

export function useGetStudentReportQuery(
  studentId: string,
  term?: string | number,
  session?: string,
) {
  return useQuery({
    queryKey: ["gradezz", studentId, term, session],
    queryFn: () => getStudentReport(studentId, term, session),
    enabled: !!studentId,
  });
}

export function useUpdateStudentReportMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateStudentReport,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["gradezz"] }).catch(() => {});
    },
  });
}

export function useSetGradeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setGradeForStudent,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["gradezz"] }).catch(() => {});
    },
  });
}

export function useSaveTraitEvaluationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveTraitEvaluation,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["gradezz"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["student-report"] }).catch(() => {});
    },
  });
}

// ---------------------------------------------------------------------------
// Expense Catalog Hooks
// ---------------------------------------------------------------------------

export function useGetExpenseCatalogQuery(params?: { search?: string }) {
  return useQuery({
    queryKey: ["expense-catalog", params],
    queryFn: () => getExpenseCatalog(params),
  });
}

export function useCreateExpenseCatalogMutation({ close }: { close?: () => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createExpenseCatalog,
    onError: () => {},
    onSuccess: () => {
      if (close) close();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-catalog"] }).catch(() => {});
    },
  });
}

export function useUpdateExpenseCatalogMutation({ close }: { close?: () => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExpenseCatalog,
    onError: () => {},
    onSuccess: () => {
      if (close) close();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-catalog"] }).catch(() => {});
    },
  });
}

export function useDeleteExpenseCatalogMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExpenseCatalog,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-catalog"] }).catch(() => {});
    },
  });
}

// ---------------------------------------------------------------------------
// Expense Vendor Hooks
// ---------------------------------------------------------------------------

export function useGetExpenseVendorsQuery(params?: { search?: string }) {
  return useQuery({
    queryKey: ["expense-vendors", params],
    queryFn: () => getExpenseVendors(params),
  });
}

export function useCreateExpenseVendorMutation({ close }: { close?: () => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createExpenseVendor,
    onError: () => {},
    onSuccess: () => {
      if (close) close();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-vendors"] }).catch(() => {});
    },
  });
}

export function useUpdateExpenseVendorMutation({ close }: { close?: () => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExpenseVendor,
    onError: () => {},
    onSuccess: () => {
      if (close) close();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-vendors"] }).catch(() => {});
    },
  });
}

export function useDeleteExpenseVendorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExpenseVendor,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-vendors"] }).catch(() => {});
    },
  });
}

// ---------------------------------------------------------------------------
// Expense Ledger Hooks
// ---------------------------------------------------------------------------

export function useGetExpensesQuery(params?: ExpenseLedgerParams) {
  return useQuery({
    queryKey: ["expenses", params],
    queryFn: () => getExpenses(params),
  });
}

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createExpense,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["expense-analytics"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["expense-catalog"] }).catch(() => {});
    },
  });
}

export function useDeleteExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExpense,
    onError: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["expense-analytics"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["expense-catalog"] }).catch(() => {});
    },
  });
}

// ---------------------------------------------------------------------------
// Expense Analytics Hook
// ---------------------------------------------------------------------------

export function useGetExpenseAnalyticsQuery(params?: ExpenseAnalyticsParams) {
  return useQuery({
    queryKey: ["expense-analytics", params],
    queryFn: () => getExpenseAnalytics(params),
  });
}

